import styles from "./Hero.module.scss";
import { StartPageType } from "@/types/ModuleOptionsType";
import { Headline } from "../../Global/Headline/Headline";
import { Button } from "../../Global/Button/Button";
import { MediaItem } from "../../Global/MediaItem/MediaItem";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { Header_Startseite } from "@/wp/types/acf-header-types";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { passACFLink } from "@/utils/passACFLink";
import { Section } from "@/components/Global/Section/Section";
import AnimationPlayer from "@/components/AnimationPlayer/AnimationPlayer";
import { OptionsBackgroundLayoutType } from "@/types/OptionType";
import { useAppContext } from "@/context/AppContext";
import { useEffect, useRef } from "react";
import { useMotionValueEvent, useScroll } from "motion/react";
import { HeaderFoam } from "../HeaderFoam/HeaderFoam";
import { Search } from "@/components/Navbar/Search/Search";

interface HeroProps {
  header: Header_Startseite["header_startseite"];
  startPageOptions: StartPageType;
  backgroundImages: OptionsBackgroundLayoutType;
  searchPageSlug: string;
}

export const Hero = ({
  header,
  startPageOptions,
  backgroundImages,
  searchPageSlug,
}: HeroProps) => {
  const { title, overline, subline, topfi_graphic } = header;

  const { setShowSearch } = useAppContext();

  useEffect(() => {
    setShowSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    button_type,
    show_overline_subline,
    second_button_type,
    two_buttons,
    show_extended_link,
  } = startPageOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const contentJSX = (
    <>
      {title && (
        <Headline
          headline_html_tag={"h1-hero"}
          headline={title}
          overline={showOverline ? overline : ""}
          subline={showSubline ? subline : ""}
        />
      )}

      <div className={`content`}>
        {header.teasertext.title && (
          <span className="title">{header.teasertext.title}</span>
        )}
        {header.teasertext.content && (
          <div
            className="intro-text"
            dangerouslySetInnerHTML={{
              __html: header.teasertext.content,
            }}
          />
        )}
        <div className={`content ${styles["links-wrapper"]}`}>
          {show_extended_link ? (
            <>
              {header.link_extended && (
                <ExtendedLink
                  extendedLink={header.link_extended}
                  btnType={
                    button_type
                      ? button_type
                      : header.link_extended.type === "email"
                        ? "secondary"
                        : "primary"
                  }
                />
              )}
              {two_buttons && header.link_2_extended && (
                <ExtendedLink
                  extendedLink={header.link_2_extended}
                  btnType={
                    button_type
                      ? button_type
                      : header.link_2_extended.type === "email"
                        ? "secondary"
                        : "primary"
                  }
                />
              )}
            </>
          ) : (
            <>
              {header.link && header.link.url && (
                <Button
                  type={button_type ?? "primary"}
                  {...passACFLink(header, "link")}
                >
                  {header.link.title}
                </Button>
              )}
              {two_buttons && header.link_2 && header.link_2.url && (
                <Button
                  type={second_button_type ?? "secondary"}
                  {...passACFLink(header, "link_2")}
                >
                  {header.link_2.title}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  const mediaJSX =
    topfi_graphic.type === "image" ? (
      <MediaItem
        image_desktop={topfi_graphic.image}
        sizes="(max-width: 450px) 50vw, 100vw"
        quality={90}
        priority={true}
        className={`auto-height`}
      />
    ) : topfi_graphic.type === "animation" && topfi_graphic.svg_animation ? (
      <AnimationPlayer url={topfi_graphic.svg_animation.url} />
    ) : (
      <></>
    );

  const FlowerJSX = (
    <>
      {topfi_graphic.image_left && (
        <div className={styles["flower-position"]}>
          <div
            style={
              {
                "--bg-image-url": `url('${topfi_graphic.image_left.url}')`,
              } as React.CSSProperties
            }
            className={styles["flower-left"]}
          ></div>
          <div className={styles["color-circle"]} />
        </div>
      )}
      {topfi_graphic.image_right && (
        <div className={styles["flower-position"]}>
          <div
            style={
              {
                "--bg-image-url": `url('${topfi_graphic.image_right.url}')`,
              } as React.CSSProperties
            }
            className={styles["flower-right"]}
          ></div>
        </div>
      )}
    </>
  );

  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    if (v > 0.8) {
      setShowSearch(true);
    } else {
      setShowSearch(false);
    }
  });

  return (
    <Section
      ref={ref}
      className={styles["startseite-header"]}
      disableColumns
      disableContainer
      disableClip
    >
      <div className="container h-100">
        <div
          className={`columns is-multiline is-vcentered h-100 ${styles["is-centered-touch"]}`}
        >
          <div
            className={`column is-12-mobile is-10-tablet is-offset-1-desktop is-offset-2-widescreen is-5-widescreen is-7-desktop ${styles["content-col"]}`}
          >
            {contentJSX}
          </div>
          <div
            className={`column is-12-mobile is-10-tablet is-4-desktop is-offset-7-desktop is-offset-0-widescreen is-4-widescreen`}
          >
            <div className={styles["media-col"]}>
              {/* <HeroSearch searchPageSlug={searchPageSlug} />
               */}
              <Search searchPageSlug={searchPageSlug} isHero />
              <div className="is-hidden-desktop">{FlowerJSX}</div>
              {mediaJSX}
            </div>
          </div>
        </div>
      </div>

      <div className={styles["flower-wrapper"]}>{FlowerJSX}</div>

      {backgroundImages.background_mixed && (
        <HeaderFoam
          backgroundImages={backgroundImages}
          foamColor="naples"
          isHeroHeader
        />
      )}
    </Section>
  );
};
